import {get} from 'lodash-es';
import React, {
    useContext, useState, FC,
} from 'react';
import {useDispatch} from 'react-redux';

import {
    selectMetadata, selectTableEntityMetadata, setMetadata, ITableEntityMeta,
} from 'entities/metadata';
import {TEntity} from 'modules/data';
import {actionSaveRows} from 'modules/data/data-actions';
import {selectFilterEntityData, selectTableEntityData} from 'modules/data/data-selectors';
import {EEntityType} from 'shared/constants/entities';
import {ERequestType} from 'shared/types/requests';
import {useAppSelector} from 'store/config/hooks';

import {ETableMode, TableModeContext} from '../../context';
import {ActionButton} from '../action-button';
import {disabledFields} from '../button-action-edit-field/button-action-edit-fields';
import {ITableActionProps} from '../table-action-types';

export const ButtonActionSaveRows: FC<ITableActionProps> = ({
    meta,
    wrapper,
    entityName,
    ...props
}: ITableActionProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const {setTableMode} = useContext(TableModeContext);
    const editRows = useAppSelector(selectTableEntityData(entityName))?.editRows;
    const {fields} = useAppSelector(selectTableEntityMetadata(entityName));
    const {data: filterData} = useAppSelector(selectFilterEntityData(entityName)) || {};
    const fieldsMeta: ITableEntityMeta = useAppSelector(selectMetadata(entityName, EEntityType.TABLE));

    const {
        referenceUrl,
        requestType,
        additionalInfoKeys,
        shouldPickAdditionalInfoKeysFromFilter: shouldPickFromFilter,
        actionIcon,
        actionTitle,
        checkForDisabledMetaFields,
    } = meta;

    const actionsForRows = async () => {
        if (additionalInfoKeys) {
            const newDate: TEntity[] = [];
            editRows?.forEach(row => {
                const valueRow: Record<string, any> = {};
                const dataToPickFrom = {
                    ...row,
                    ...(shouldPickFromFilter ? filterData : {}),
                };
                additionalInfoKeys
                    ?.filter((key: string) => Object.keys(dataToPickFrom).includes(key))
                    ?.forEach((key: string) => {
                        valueRow[key] = typeof dataToPickFrom[key] === 'object'
                            ? get(dataToPickFrom[key], 'value')
                            : dataToPickFrom[key];
                    });
                newDate.push(valueRow);
            });
            dispatch(
                actionSaveRows(
                    entityName,
                    referenceUrl || entityName,
                    requestType || ERequestType.POST,
                    newDate,
                ),
            );
        }
    };

    const handleClick = () => {
        (async () => {
            setIsLoading(true);
            if (checkForDisabledMetaFields) {
                if (fields?.some(field => field.isDisabled)) return;
                const newMetaData = disabledFields(fieldsMeta, true);
                await actionsForRows();
                dispatch(setMetadata({entityName, entityType: EEntityType.TABLE, metadata: newMetaData}));
                setTableMode(ETableMode.VIEW);
                return;
            }
            await actionsForRows();
            setTableMode(ETableMode.VIEW);
            setIsLoading(false);
        })();
    };
    const Component = wrapper || ActionButton;
    return (
        <Component
            {...props}
            onClick={handleClick}
            actionIcon={actionIcon}
            disabled={!editRows?.length || isLoading}
        >
            {actionTitle}
        </Component>
    );
};
